import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { jssPreset, StylesProvider, ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

import theme from './Backbone/themes';
import Routes from './Route';
import Snackbar from './Control/Snackbar';
import NavigationScroll from './Backbone/layout/NavigationScroll';

import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

import { create } from 'jss';
import rtl from 'jss-rtl';
import { RootReduxState } from './Redux/Store';
import { KsUtility } from './Core';
import enMessages from './Localization/en.json';
import ErrorBoundary from './Control/ErrorBoundary';

const jss = create({
    plugins: [...jssPreset().plugins, rtl()]
});

type StylisPluginType = NonNullable<Parameters<typeof createCache>[0]['stylisPlugins']>[number];

const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [rtlPlugin as unknown as StylisPluginType]
});

const loadLocaleData = (locale: string) => {
    switch (locale) {
        case 'bn':
            return import('./Localization/bn.json');
        default:
            return Promise.resolve({ default: enMessages });
    }
};

const defaultEnglishMessages = KsUtility.flattenMessages(enMessages);

const App = () => {
    const customization = useSelector((state: RootReduxState) => state.customization);
    const [messages, setMessages] = useState<Record<string, string>>(defaultEnglishMessages);

    useEffect(() => {
        if (!customization.locale || customization.locale === 'en') {
            setMessages(defaultEnglishMessages);
            return;
        }
        loadLocaleData(customization.locale)
            .then((d: { default: Record<string, unknown> }) => {
                setMessages(KsUtility.flattenMessages(d.default));
            })
            .catch(err => {
                console.warn('[App] Failed to load locale data, falling back to English:', err);
                setMessages(defaultEnglishMessages);
            });
    }, [customization.locale]);

    if (customization.rtlLayout) {
        document.querySelector('body')!.setAttribute('dir', 'rtl');
    }

    return (
        <ErrorBoundary>
            <StylesProvider jss={jss}>
                <CacheProvider value={cacheRtl}>
                    <IntlProvider locale={customization.locale || 'en'} defaultLocale="en" messages={messages || defaultEnglishMessages}>
                        <NavigationScroll>
                            <ThemeProvider theme={theme(customization)}>
                                <CssBaseline />
                                <Routes />
                                <Snackbar />
                            </ThemeProvider>
                        </NavigationScroll>
                    </IntlProvider>
                </CacheProvider>
            </StylesProvider>
        </ErrorBoundary>
    );
};

export default App;
